// dealRollup.ts — pure helpers for the deal/project activities roll-up
// (deal-project-rollup-v1). Framework-agnostic (no D1, no Hono, no env) so the
// discriminator + merge/slice mechanics can be unit-tested in isolation (see
// dealRollup.test.ts). Imported by api/src/index.ts exactly like the other
// src/lib helpers the worker reuses (buildOwnCallsScope precedent).
//
// THE SHARP TRAP — the owner_type_id === 3 discriminator:
//   `owner_type_id = 3` is OVERLOADED. It means a PROJECT-owned activity ONLY
//   when `owner_id` is a non-empty string other than "0" (a real project id).
//   `owner_type_id = 3` with a NULL / empty / 0 / "0" `owner_id` is a
//   CONTACT-ONLY row (written by call-start / webhook / email logger before any
//   deal/project assignment). Every reader that wants "project-owned rows" MUST
//   go through isProjectOwnedRow — never test `owner_type_id === 3` alone.

export interface DealTimelineRow {
  /** ISO-ish timestamp the merged timeline sorts on (DESC). */
  created?: string | null
  /** 1 = lead, 2 = deal, 3 = project-OR-contact (see isProjectOwnedRow). */
  owner_type_id?: number | string | null
  /** Project id (TEXT) for project rows; NULL/0 for contact-only 3-rows. */
  owner_id?: number | string | null
  [k: string]: unknown
}

/**
 * True only for genuine PROJECT-owned activity rows: `owner_type_id === 3` AND
 * `owner_id` is a non-empty string that isn't "0".
 *
 * Rejects the contact-only overloads of owner_type_id 3:
 *   - owner_id NULL / undefined  → contact-only
 *   - owner_id "" (empty)        → contact-only
 *   - owner_id 0 (number)        → contact-only
 *   - owner_id "0" (string)      → contact-only
 * Accepts TEXT project ids (e.g. "proj_abc", "42x", any non-"0" non-empty id).
 */
export function isProjectOwnedRow(row: DealTimelineRow | null | undefined): boolean {
  if (!row) return false
  if (Number(row.owner_type_id) !== 3) return false
  const raw = row.owner_id
  if (raw === null || raw === undefined) return false
  const s = String(raw).trim()
  if (s === '' || s === '0') return false
  return true
}

/**
 * Merge N already-fetched source arrays into a single created-DESC timeline and
 * return the [offset, offset + limit) page window. Mirrors the mechanics of the
 * project-activities endpoint (api/src/index.ts GET
 * /api/crm/projects/:id/activities): concat all sources, stable-sort by `created`
 * DESC via localeCompare, then slice the page window. V8's Array.sort is stable,
 * so rows with equal `created` keep their source/insertion order.
 */
export function mergeDealTimeline<T extends DealTimelineRow>(
  sources: T[][],
  opts: { limit: number; offset: number }
): T[] {
  const limit = Math.max(0, Number(opts?.limit) || 0)
  const offset = Math.max(0, Number(opts?.offset) || 0)
  const merged: T[] = []
  for (const src of sources || []) {
    if (Array.isArray(src)) merged.push(...src)
  }
  merged.sort((a, b) => String(b?.created || '').localeCompare(String(a?.created || '')))
  return merged.slice(offset, offset + limit)
}
